'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Users,
  Target,
  Mail,
  BarChart3,
  History,
  Shield,
  CheckCircle2,
  ArrowRight,
  Zap,
  Database,
  LineChart,
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const features = [
  {
    icon: Users,
    title: '統合顧客管理',
    description: '地域住民、会員、事業者を一元管理。属性・行動履歴・関係性を可視化します。'
  },
  {
    icon: Target,
    title: 'セグメント機能',
    description: '年齢、地域、興味関心、行動履歴など多様な条件で柔軟にセグメント作成。'
  },
  {
    icon: Mail,
    title: 'メール・LINE連携',
    description: 'セグメントに応じた最適なメッセージを、適切なチャネルで配信。'
  },
  {
    icon: History,
    title: '施策履歴管理',
    description: '過去の施策と効果を蓄積。成功パターンを発見し、再現性を高めます。'
  },
  {
    icon: BarChart3,
    title: 'KPIダッシュボード',
    description: 'エンゲージメント率、再来率など重要指標をリアルタイムで可視化。'
  },
  {
    icon: Shield,
    title: 'セキュリティ',
    description: '個人情報保護法準拠。部門別アクセス制御で安全な情報管理を実現。'
  }
]

const useCases = [
  {
    title: '地域イベントの参加者管理',
    scenario: '地域で開催する各種イベントの参加者情報を一元管理し、リピート参加を促進',
    benefits: [
      '参加履歴からの興味分析',
      'パーソナライズされた案内',
      '参加率30%向上'
    ]
  },
  {
    title: '商店街の顧客管理',
    scenario: '加盟店舗の顧客情報を統合し、商店街全体でのマーケティングを実現',
    benefits: [
      '購買履歴の横断分析',
      '効果的なクーポン配信',
      '来店頻度25%増加'
    ]
  },
  {
    title: '観光客のリピート促進',
    scenario: '観光客の体験履歴を管理し、再訪を促すコミュニケーションを展開',
    benefits: [
      '季節に応じた情報提供',
      'ロイヤルティプログラム',
      'リピート率40%向上'
    ]
  }
]

export function CrmProductContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] opacity-20"></div>
        <div
          className="animate-fade-up container relative z-10"
        >
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">地域・会員・事業者の関係管理</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              CRM
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              再来・再参加をつくるコミュニケーション基盤
            </p>
            <p className="text-lg mb-8 opacity-80">
              顧客・会員・事業者の関係管理を一元化。<br />
              セグメント分析から施策実行、効果測定まで、<br />
              地域のエンゲージメントを継続的に高めます。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="secondary">
                無料デモを申し込む
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                資料ダウンロード
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 100" className="w-full h-24 fill-background">
            <path d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      {/* 価値提案 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">
              なぜCodeFoxのCRMなのか
            </h2>
            <p className="text-lg text-muted-foreground">
              地域特有の複雑な関係性を理解し、持続的なエンゲージメントを実現
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Database,
                title: '統合データベース',
                description: '住民、観光客、事業者など多様なステークホルダーを一つのプラットフォームで管理'
              },
              {
                icon: Zap,
                title: '自動化されたコミュニケーション',
                description: '行動トリガーやスケジュールに基づく自動配信で、運用負荷を大幅削減'
              },
              {
                icon: LineChart,
                title: '成果の可視化',
                description: '施策の効果を定量的に把握し、PDCAサイクルを高速化'
              }
            ].map((value, index) => {
              const Icon = value.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <CardTitle>{value.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{value.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 主要機能 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">主要機能</h2>
            <p className="text-lg text-muted-foreground">
              地域のコミュニケーションを最適化する充実した機能群
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <FadeUp delay={index * 0.05}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 活用シーン */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">活用シーン</h2>
            <p className="text-lg text-muted-foreground">
              様々な地域課題に対応する柔軟な活用が可能
            </p>
          </FadeUp>

          <Tabs defaultValue="event" className="max-w-4xl mx-auto">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="event">イベント管理</TabsTrigger>
              <TabsTrigger value="shopping">商店街活性化</TabsTrigger>
              <TabsTrigger value="tourism">観光促進</TabsTrigger>
            </TabsList>

            {useCases.map((useCase, index) => (
              <TabsContent 
                key={index} 
                value={index === 0 ? 'event' : index === 1 ? 'shopping' : 'tourism'}
                className="mt-8"
              >
                <Card>
                  <CardHeader>
                    <CardTitle>{useCase.title}</CardTitle>
                    <CardDescription>{useCase.scenario}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">導入効果</h4>
                        <ul className="space-y-2">
                          {useCase.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <CheckCircle2 className="h-5 w-5 text-primary" />
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Button className="w-full">
                        この事例の詳細を見る
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* 導入の流れ */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">導入の流れ</h2>
            <p className="text-lg text-muted-foreground">
              スムーズな導入と確実な定着をサポート
            </p>
          </FadeUp>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: '要件ヒアリング',
                  description: '現状の課題と目指すゴールを詳しくお伺いします',
                  duration: '1週間'
                },
                {
                  step: '02',
                  title: 'サンドボックス環境',
                  description: '実際のデータを使った検証環境で効果を確認',
                  duration: '2週間'
                },
                {
                  step: '03',
                  title: '小規模運用',
                  description: '限定的な範囲で運用を開始し、改善点を洗い出し',
                  duration: '1ヶ月'
                },
                {
                  step: '04',
                  title: '本番運用',
                  description: '全体展開と継続的な改善サポート',
                  duration: '継続'
                }
              ].map((phase, index) => (
                <FadeUp delay={index * 0.1}
                  key={index}
                  className="flex gap-6"
                >
                  <div className="shrink-0">
                    <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                      {phase.step}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{phase.title}</h3>
                    <p className="text-muted-foreground mb-1">{phase.description}</p>
                    <Badge variant="secondary">{phase.duration}</Badge>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 料金プラン */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">料金プラン</h2>
            <p className="text-lg text-muted-foreground">
              規模に応じて選べる柔軟なプラン
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'スターター',
                price: '月額 5万円〜',
                description: '小規模な団体・商店街向け',
                features: [
                  '登録数: 〜1,000件',
                  '基本的なセグメント機能',
                  'メール配信',
                  '月次レポート'
                ]
              },
              {
                name: 'スタンダード',
                price: '月額 15万円〜',
                description: '中規模自治体・観光協会向け',
                features: [
                  '登録数: 〜10,000件',
                  '高度なセグメント機能',
                  'メール・LINE配信',
                  'リアルタイムダッシュボード',
                  'API連携'
                ],
                popular: true
              },
              {
                name: 'エンタープライズ',
                price: 'お問い合わせ',
                description: '大規模自治体・広域連携向け',
                features: [
                  '登録数: 無制限',
                  'カスタムセグメント',
                  'マルチチャネル配信',
                  'カスタムダッシュボード',
                  '専任サポート'
                ]
              }
            ].map((plan, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card className={`h-full ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                  {plan.popular && (
                    <div className="bg-primary text-white text-center py-2 text-sm font-semibold">
                      人気プラン
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="text-3xl font-bold mt-4">{plan.price}</div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6" variant={plan.popular ? 'default' : 'outline'}>
                      {plan.name === 'エンタープライズ' ? 'お問い合わせ' : '無料トライアル'}
                    </Button>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold mb-4">
              地域の関係性を、もっと豊かに
            </h2>
            <p className="text-xl mb-8 opacity-90">
              まずは無料デモで、CRMがもたらす変化を体験してください
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary">
                無料デモを申し込む
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                詳しい資料を見る
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}